/**
 * Footer Mobile Collapsible Groups & Menus
 * Makes both groups and menus collapsible on mobile devices
 */

class FooterMobileCollapsible {
  constructor() {
    this.isMobile = window.innerWidth <= 749;
    this.init();
    this.bindEvents();
  }

  init() {
    if (!this.isMobile) return;
    
    this.setupCollapsibleGroups();
    this.setupCollapsibleMenus();
  }

  setupCollapsibleGroups() {
    const footerGroups = document.querySelectorAll('footer .group');
    
    footerGroups.forEach((group, index) => {
      // Skip if group is already processed
      if (group.hasAttribute('data-collapsible-processed')) return;
      
      // Check if this group has accordion enabled
      const accordionEnabled = group.getAttribute('data-accordion-enabled') === 'true';
      
      if (!accordionEnabled) {
        // Skip groups that don't have accordion enabled
        return;
      }
      
      // Get accordion title or use first text element
      let groupTitle = group.getAttribute('data-accordion-title');
      if (!groupTitle) {
        const firstTextBlock = group.querySelector('h1, h2, h3, h4, h5, h6, p');
        groupTitle = firstTextBlock ? firstTextBlock.textContent.trim() : `Section ${index + 1}`;
      }
      
      // Set group title as data attribute
      group.setAttribute('data-group-title', groupTitle);
      group.setAttribute('data-collapsible-processed', 'true');
      
      // If group already has accordion structure, skip manual setup
      if (group.querySelector('.group__details')) {
        return;
      }
      
      // Hide content initially for manual setup
      const contentElements = Array.from(group.children);
      contentElements.forEach(el => {
        el.style.display = 'none';
      });
      
      // Add click handler to the group element
      group.addEventListener('click', (e) => {
        // Only handle clicks on the pseudo-element area (header)
        const rect = group.getBoundingClientRect();
        const clickY = e.clientY - rect.top;
        
        // Check if click is in the header area (first 50px approximately)
        if (clickY <= 50) {
          this.toggleGroup(group);
        }
      });
    });
  }

  setupCollapsibleMenus() {
    // The existing accordion functionality should work
    // But we can enhance it for consistency
    const footerMenus = document.querySelectorAll('footer .menu');
    
    footerMenus.forEach(menu => {
      // Ensure accordion behavior is enabled
      if (!menu.classList.contains('menu--accordion')) {
        menu.classList.add('menu--accordion', 'menu--plus');
        
        const details = menu.querySelector('.menu__details');
        if (details) {
          details.removeAttribute('open');
        }
      }
    });
  }

  toggleGroup(group) {
    const isOpen = group.classList.contains('is-open');
    const contentElements = Array.from(group.children);
    
    if (isOpen) {
      // Close group
      group.classList.remove('is-open');
      contentElements.forEach(el => {
        el.style.display = 'none';
      });
    } else {
      // Open group
      group.classList.add('is-open');
      contentElements.forEach(el => {
        el.style.display = 'block';
      });
    }
  }

  bindEvents() {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 749;
      
      if (wasMobile !== this.isMobile) {
        if (this.isMobile) {
          this.init();
        } else {
          this.cleanup();
        }
      }
    });
  }

  cleanup() {
    // Remove mobile-specific modifications when switching to desktop
    const footerGroups = document.querySelectorAll('footer .group[data-collapsible-processed]');
    
    footerGroups.forEach(group => {
      group.classList.remove('is-open');
      group.removeAttribute('data-group-title');
      group.removeAttribute('data-collapsible-processed');
      
      // Show all content on desktop
      const contentElements = Array.from(group.children);
      contentElements.forEach(el => {
        el.style.display = '';
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FooterMobileCollapsible();
  });
} else {
  new FooterMobileCollapsible();
}